import React, { useEffect, useState } from "react";
import {
  Box,
  PageLayout,
  Text,
  ActionList,
  ActionMenu,
  FormControl,
  Select,
  TextInput,
} from "@primer/react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  SyncIcon,
  KebabHorizontalIcon,
} from "@primer/octicons-react";
import { Dialog } from "@primer/react/drafts";
import { useSelector } from "react-redux";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import { kyThiApi } from "../../api/kyThiApi";
import { kyThiMonThiApi } from "../../api/kyThiMonThiApi";
import { monThiApi } from "../../api/monThiApi";
import { truongApi } from "../../api/truongApi";
import DataTable from "../../components/ui/data-table";
import MyButton from "../../components/ui/button/Button";
import AddMonThi from "./detail_add_monthi";
import KyThiMonThiDetailModal from "./detail_kythi_monthi";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { ComboboxTruong } from "../../components/combobox-truong";
import { dm_truong } from "../../models/response/category/dm_truong";
import ComboboxKyThi from "../../components/combobox-kythi";

const KyThiMonThi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [kyThis, setKyThis] = useState([]);
  const [selectedKyThi, setSelectedKyThi] = useState<any>(null);
  const [monThis, setMonThis] = useState([]);
  const [selectedMonThi, setSelectedMonThi] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [truongs, setTruongs] = useState<dm_truong[]>([]);
  const [selectedTruong, setSelectedTruong] = useState<dm_truong | null>(null);

  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const loadTruongs = async () => {
    try {
      const res = await truongApi.selectAll();
      if (res.is_success) {
        const filtered = res.data.filter(
          (x: dm_truong) => x.dm_coso_id === dm_coso_id
        );
        setTruongs(filtered);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
  };

  const loadKyThis = async () => {
    setIsLoading(true);
    try {
      const res = await kyThiApi.selectAll();
      if (res.is_success) {
        // Đầu tiên lọc theo cơ sở và năm học
        let filtered = res.data.filter(
          (x: any) => x.dm_coso_id === dm_coso_id && x.nam_hoc === nam_hoc
        );

        // Nếu có trường được chọn thì lọc thêm theo trường
        if (selectedTruong) {
          filtered = filtered.filter(
            (x: any) => x.dm_truong_id === selectedTruong.id
          );
        }

        setKyThis(filtered);
        // Reset selected kỳ thi when trường changes
        setSelectedKyThi(null);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
    setIsLoading(false);
  };

  const loadMonThis = async () => {
    if (!selectedKyThi?.id) {
      setMonThis([]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await monThiApi.selectByKyThi(selectedKyThi.id);
      if (res.is_success) {
        setMonThis(res.data);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadTruongs();
  }, [dm_coso_id]);

  // Load kỳ thi when trường changes
  useEffect(() => {
    loadKyThis();
  }, [selectedTruong]);

  // Load môn thi when kỳ thi changes
  useEffect(() => {
    loadMonThis();
  }, [selectedKyThi]);

  const handleDelete = async (id: number) => {
    try {
      const res = await kyThiMonThiApi.delete(id);
      if (res.is_success) {
        await loadMonThis();
        NotifyHelper.Success(translate("Success.Delete"));
      } else {
        NotifyHelper.Error(res.message || translate("Error.Generic"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.Generic"));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <DataTable
        height="calc(100vh - 200px)"
        subTitle={`${translate("Base.Label.TotalCount")}: ${monThis.length}`}

        data={monThis}
        isLoading={isLoading}
        title={translate("Danh sách môn thi")}
        searchEnable
        paging={{ enable: true }}
        actionComponent={
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <Box sx={{ width: "200px", mr: 2 }}>
              <FormControl>
                <ComboboxTruong
                  value={selectedTruong?.id}
                  onValueChanged={(id) => {
                    const truong = truongs.find((t) => t.id === id);
                    setSelectedTruong(truong || null);
                    // Reset kỳ thi khi đổi trường
                    setSelectedKyThi(null);
                  }}
                  isShowClearButton={true}
                />
              </FormControl>
            </Box>

            <Box sx={{ width: "400px", mr: 2 }}>
              <FormControl>
                <ComboboxKyThi
                  value={selectedKyThi?.id}
                  dm_truong_id={selectedTruong?.id}
                  onValueChanged={(id, data) => {
                    setSelectedKyThi(data);
                  }}
                  isShowClearButton={true}
                />
              </FormControl>
            </Box>

            <MyButton
              text={translate("Thêm môn thi")}
              leadingVisual={PlusIcon}
              size="medium"
              variant="primary"
              onClick={() => setShowAddModal(true)}
              disabled={!selectedKyThi}
            />
          </Box>
        }
        columns={[
          {
            id: "actions",
            caption: "",
            width: "50px",
            cellRender: (data: any) => (
              <ActionMenu>
                <ActionMenu.Button
                  icon={KebabHorizontalIcon}
                  variant="invisible"
                  sx={{ m: -1 }}
                >
                  &nbsp;
                </ActionMenu.Button>
                <ActionMenu.Overlay width="auto">
                  <ActionList>
                    <ActionList.Item
                      onSelect={() => {
                        setSelectedMonThi(data);
                        setShowEditModal(true);
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <PencilIcon />
                      </ActionList.LeadingVisual>
                      {translate("Sửa")}
                    </ActionList.Item>
                    <ActionList.Item
                      variant="danger"
                      onSelect={() => handleDelete(data.id)}
                    >
                      <ActionList.LeadingVisual>
                        <TrashIcon />
                      </ActionList.LeadingVisual>
                      {translate("Xóa")}
                    </ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            ),
          },
          {
            dataField: "mon_thi",
            caption: translate("Môn thi"),
          },
          {
            dataField: "mon_thi_en",
            caption: translate("Môn thi(En)"),
          },
          {
            dataField: "thang_diem",
            caption: translate("Thang điểm"),
          },
          {
            dataField: "ex_hinhthucthi",
            caption: translate("Hình thức thi"),
          },
          {
            dataField: "ngay_thi_converted",
            caption: translate("Ngày thi"),
          },
          {
            dataField: "gio_bat_dau_converted",
            caption: translate("Giờ bắt đầu"),
          },
          {
            dataField: "gio_ket_thuc_converted",
            caption: translate("Giờ kết thúc"),
          },
          
        ]}
      />

      {showAddModal && selectedKyThi && (
        <AddMonThi
          ex_kythi_id={selectedKyThi.id}
          onCancel={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadMonThis();
          }}
        />
      )}

      {showEditModal && selectedMonThi && (
        <KyThiMonThiDetailModal
          id={selectedMonThi.id}
          onCancel={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            loadMonThis();
          }}
        />
      )}

      {showDeleteConfirm && selectedMonThi && (
        <Dialog
          title={translate("Confirm.Delete.Title")}
          onClose={() => setShowDeleteConfirm(false)}
        >
          <Dialog.Body>{translate("Confirm.Delete.Message")}</Dialog.Body>
          <Dialog.Footer>
            <MyButton
              text={translate("Cancel")}
              onClick={() => setShowDeleteConfirm(false)}
            />
            <MyButton
              variant="danger"
              text={translate("Delete")}
              onClick={() => handleDelete(selectedMonThi.id)}
            />
          </Dialog.Footer>
        </Dialog>
      )}
    </Box>
  );
};

export default KyThiMonThi;
